/**
 * App footer — compact DSS chrome, not a marketing block.
 */
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Link,
  Stack,
  Divider,
} from '@mui/material';
import Logo from './common/Logo';
import { otherLanguageOf } from '../seo/locale';
import { OPEN_CONSENT_EVENT } from '../services/consent';

const year = new Date().getFullYear();

const Footer: React.FC = () => {
  const { pathname } = useLocation();
  // Landing has its own branding block — keep app shell footer only inside product
  const isPublicMarketing = pathname === '/' || pathname.startsWith('/login') || pathname.startsWith('/register');

  const other = otherLanguageOf(pathname);

  if (isPublicMarketing) {
    return null;
  }

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        py: 1.5,
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.25, sm: 2 }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Logo type="flahafast" size="small" />
            <Box>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.primary"
                lineHeight={1.2}
              >
                FlahaFAST
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 0.75, fontWeight: 500 }}
                >
                  DSS
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Nutrient formulation decision support
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{
              '& a, & button': {
                cursor: 'pointer',
                typography: 'caption',
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: 'primary.main' },
              },
            }}
          >
            <Link component={RouterLink} to="/dashboard">
              Workspace
            </Link>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
            <Link component={RouterLink} to="/support">
              Support
            </Link>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
            <Link component={RouterLink} to="/contact">
              Contact
            </Link>
            <Link component={RouterLink} to="/pricing">
              Pricing
            </Link>
            <Link component={RouterLink} to="/tools/ec-ppm-converter">
              Free tools
            </Link>
            <Link component={RouterLink} to="/guides">
              Crop recipes
            </Link>
            <Link component={RouterLink} to="/glossary">
              Glossary
            </Link>
            {other && (
              <Link component={RouterLink} to={other.path} lang={other.lang} hrefLang={other.lang}>
                {other.lang === 'ar' ? 'العربية' : 'English'}
              </Link>
            )}
            <Link component={RouterLink} to="/about">
              About
            </Link>
            <Link component={RouterLink} to="/privacy">
              Privacy
            </Link>
            <Link component={RouterLink} to="/terms">
              Terms
            </Link>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
            <Link component="button" type="button" onClick={() => window.dispatchEvent(new Event(OPEN_CONSENT_EVENT))}>
              Cookie settings
            </Link>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
            <Typography variant="caption" color="text.disabled">
              © {year} Flaha Agri Tech
            </Typography>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
